import { Box, Flex, Heading, List, ListIcon, ListItem, Tag, TagLabel, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { AI_MARKETER_ANALYTICS_EVENT, COPY, FREE_COURSE_KEY, FREE_COURSE_SLUG } from '~/features/ai-marketer/constants';
import { GiftBoxIcon } from '~/features/ai-marketer/icons';
import { trackEvent } from '~/features/analytics/track-event';
import { CheckIcon, CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCourseLink } from '~/features/program/hooks/use-course-link';

export function FreeCourseSection() {
  const courseLink = useCourseLink({ courseSlug: FREE_COURSE_SLUG });

  return (
    <Box as='section' bg='white' py={{ base: '40px', md: '80px' }}>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex
          alignItems={{ base: 'stretch', lg: 'center' }}
          flexDirection={{ base: 'column', lg: 'row' }}
          gap={{ base: '24px', lg: 0 }}
        >
          <Flex
            border={{ base: 'none', lg: '1px solid' }}
            borderColor={{ lg: 'grey.200' }}
            borderLeftRadius={{ lg: '8px' }}
            borderRight={{ lg: 'none' }}
            flex='1'
            flexDirection='column'
            gap={{ base: '18px', lg: '24px' }}
            p={{ base: 0, lg: '48px 48px 56px' }}
          >
            <Flex flexDirection='column' gap={{ base: '12px', lg: '24px' }}>
              <FreeCourseTag />
              <Heading
                as='h2'
                color='grey.900'
                letterSpacing='-0.02em'
                size={{ base: 'mobile-heading-2', md: 'heading-2' }}
              >
                {COPY.freeCourse.title}
              </Heading>
            </Flex>
            <Text
              color='grey.500'
              fontSize={{ base: '0.875rem', lg: '1rem' }}
              letterSpacing={{ base: '-0.01em', lg: 0 }}
              lineHeight={{ base: '1.4', lg: '1.5' }}
              maxWidth={{ base: '340px', lg: 'none' }}
            >
              {COPY.freeCourse.description}
            </Text>
          </Flex>
          <Flex
            bgGradient='linear(to-br, #031643 0%, #00298C 50%, #6100B0 100%)'
            borderRadius='8px'
            color='white'
            flex={{ lg: '0 0 438px' }}
            flexDirection='column'
            gap={{ base: '16px', lg: '24px' }}
            overflow='hidden'
            p={{ base: '24px', lg: '32px' }}
            position='relative'
          >
            <Box
              bg='repeating-linear-gradient(135deg, transparent 0 20px, rgba(123, 162, 255, 0.08) 20px 40px)'
              bottom={0}
              clipPath='polygon(100% 0, 100% 100%, 0 100%)'
              h='180px'
              pointerEvents='none'
              position='absolute'
              right={0}
              w='180px'
              aria-hidden
            />
            <Heading
              as='h3'
              borderBottom='1px solid'
              borderColor='whiteAlpha.300'
              fontFamily='heading'
              fontSize={{ base: '1.375rem', lg: '1.5rem' }}
              fontWeight='600'
              letterSpacing={{ base: 0, lg: '-0.01em' }}
              lineHeight={{ base: '1.65rem', lg: '1.725rem' }}
              pb={{ base: '16px', lg: '24px' }}
            >
              {COPY.freeCourse.whatYoullBuild}
            </Heading>
            <List position='relative' spacing={{ base: '16px', lg: '24px' }}>
              {COPY.freeCourse.bullets.map((bullet) => (
                <ListItem key={bullet} alignItems='flex-start' display='flex' gap='12px'>
                  <ListIcon
                    as={CheckIcon}
                    color='blue.300'
                    height='18px'
                    mr={0}
                    mt={{ base: '2px', lg: '5px' }}
                    width='18px'
                  />
                  <Text fontSize={{ base: '1rem', lg: '1.125rem' }} lineHeight={{ base: '1.4', lg: '1.5' }}>
                    {bullet}
                  </Text>
                </ListItem>
              ))}
            </List>
            <Flex flexDirection='column' gap='24px' position='relative'>
              <Text
                color='#AFC6FF'
                fontSize={{ base: '0.875rem', lg: '0.8125rem' }}
                fontStyle='italic'
                lineHeight='1.4'
              >
                {COPY.freeCourse.footnote}
              </Text>
              <ButtonLink
                linkProps={{ href: courseLink }}
                buttonProps={{
                  leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
                  size: 'icon-left',
                  variant: 'solid',
                  colorScheme: 'blue',
                  width: '100%',
                  onClick: () =>
                    trackEvent({
                      type: 'click',
                      name: AI_MARKETER_ANALYTICS_EVENT,
                      label: 'free_course_enroll',
                      additionalAttributes: {
                        product_ids: [FREE_COURSE_KEY],
                      },
                    }),
                }}
              >
                {COPY.freeCourse.enroll}
              </ButtonLink>
            </Flex>
          </Flex>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function FreeCourseTag() {
  return (
    <Tag
      bgGradient='linear(to-r, #0D0D0E, #00298C)'
      border='1px solid'
      borderColor='white'
      borderRadius='4px'
      gap={{ base: '12px', lg: '16px' }}
      overflow='hidden'
      p={{ base: '7px 12px 7px 10px', lg: '10px 20px 10px 16px' }}
      position='relative'
      variant='no-stroke'
      width='fit-content'
    >
      <Box
        bg='#931CF5'
        borderRadius='full'
        filter='blur(40px)'
        h={{ base: '71px', lg: '88px' }}
        left={{ base: '-19px', lg: '-24px' }}
        opacity={0.8}
        pointerEvents='none'
        position='absolute'
        top={{ base: '-28px', lg: '-31px' }}
        w={{ base: '71px', lg: '88px' }}
        aria-hidden
      />
      <GiftBoxIcon
        color='white'
        height={{ base: '15px', lg: '22px' }}
        position='relative'
        width={{ base: '15px', lg: '22px' }}
      />
      <TagLabel
        color='white'
        fontFamily='condensed'
        fontSize={{ base: '0.875rem', lg: '1.25rem' }}
        fontWeight='500'
        letterSpacing='0.08em'
        lineHeight='1'
        position='relative'
        textTransform='uppercase'
      >
        {COPY.freeCourse.eyebrow}
      </TagLabel>
    </Tag>
  );
}
